import React, { useState } from 'react';
import { Button, Card, Col, Row } from "react-bootstrap";
import {
    FaInstagram, FaSpotify, FaTiktok, FaYoutube,
    FaArrowLeft, FaArrowRight, FaInfoCircle
} from "react-icons/fa";
import { motion, animate, useMotionValue } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import "../../styles/artist.css";
import "../../App.css";

const Artist = ({ artist }) => {
    // ✅ Always call hooks at the top
    const [showInfo, setShowInfo] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const isMobile = useMediaQuery({ maxWidth: 768 });
    const x = useMotionValue(0);
    const threshold = 150;

    // ✅ Early return after hooks
    if (!artist) {
        return <p style={{ color: 'red', textAlign: 'center' }}>Artist-Daten fehlen.</p>;
    }

    const {
        name,
        image,
        title,
        release = [],
        block1,
        block2,
        block3,
        block4,
        block5,
        spotifyLink,
        tiktokLink,
        youtubeLink,
        instagramLink
    } = artist;

    const prevImage = () => {
        if (currentIndex > 0) {
            setCurrentIndex(prev => prev - 1);
            animate(x, 0, { type: "spring", stiffness: 300, damping: 30 });
        }
    };

    const nextImage = () => {
        if (currentIndex < release.length - 1) {
            setCurrentIndex(prev => prev + 1);
            animate(x, 0, { type: "spring", stiffness: 300, damping: 30 });
        }
    };

    return (
        <Card className="section-card" style={{ background: "transparent" }}>
            <Card.Body>
                <Card.Title>
                    <Row>
                        <h1>{title}</h1>
                    </Row>
                </Card.Title>

                <Row className="justify-content-center" style={{ marginTop: "15px" }}>
                    <Col md={4}>
                        <img
                            src={image}
                            alt={name}
                            className="img-fluid"
                            style={{
                                width: "auto",
                                aspectRatio: "1 / 1",
                                objectFit: "cover",
                                borderRadius: "30px"
                            }}
                        />
                    </Col>
                </Row>

                <Row className="justify-content-center">
                    <Col md={4}>
                        <div className="d-flex justify-content-center gap-3 my-3">
                            {instagramLink && (
                                <a href={instagramLink} target="_blank" rel="noopener noreferrer"
                                   className="text-white">
                                    <FaInstagram size={24}/>
                                </a>
                            )}
                            {tiktokLink && (
                                <a href={tiktokLink} target="_blank" rel="noopener noreferrer" className="text-white">
                                    <FaTiktok size={24}/>
                                </a>
                            )}
                            {youtubeLink && (
                                <a href={youtubeLink} target="_blank" rel="noopener noreferrer" className="text-white">
                                    <FaYoutube size={24}/>
                                </a>
                            )}
                            {spotifyLink && (
                                <a href={spotifyLink} target="_blank" rel="noopener noreferrer" className="text-white">
                                    <FaSpotify size={24}/>
                                </a>
                            )}
                        </div>
                    </Col>
                </Row>

                {release.length > 0 && (
                    <>
                        <Row style={{marginTop: "20px"}}>
                            <h2>Release</h2>
                        </Row>

                        <motion.div
                            className="carousel-container"
                            drag="x"
                            dragConstraints={{
                                left: currentIndex === release.length - 1 ? 0 : -100,
                                right: currentIndex === 0 ? 0 : 200,
                            }}
                            style={{x}}
                            onDragEnd={(_, info) => {
                                if (info.offset.x < -threshold && currentIndex < release.length - 1) nextImage();
                                else if (info.offset.x > threshold && currentIndex > 0) prevImage();
                                else animate(x, 0, { type: "spring", stiffness: 300, damping: 30 });
                            }}
                        >
                            <Row className="responsive-release mobile-row d-flex justify-content-center text-center">
                                <h3>{release[currentIndex].title}</h3>

                                {!isMobile && (
                                    <Col xs={1} className="button-left d-flex align-items-center justify-content-center">
                                        <Button
                                            className="small-arrow-button"
                                            variant="dark"
                                            onClick={prevImage}
                                            disabled={currentIndex === 0}
                                        >
                                            <FaArrowLeft size={24} />
                                        </Button>
                                    </Col>
                                )}

                                <Col md={10} className="mobile-col d-flex align-items-center justify-content-center">
                                    <Row className="text-center d-flex justify-content-center align-items-center">
                                        <div className="image-container">
                                            {currentIndex === 0 ? (
                                                <motion.img
                                                    src=""
                                                    alt=""
                                                    className="carousel-image dummy"
                                                    animate={{ scale: 0.8, opacity: 0.3 }}
                                                />
                                            ) : (
                                                <motion.img
                                                    src={release[currentIndex - 1]?.image}
                                                    alt="Previous"
                                                    className="carousel-image prev"
                                                    animate={{ scale: 0.8, opacity: 0.5 }}
                                                />
                                            )}

                                            <a href={release[currentIndex].link}>
                                                <motion.img
                                                    src={release[currentIndex].image}
                                                    alt={release[currentIndex].title}
                                                    className="carousel-image current"
                                                    animate={{ scale: 1, opacity: 1 }}
                                                    transition={{ type: "spring", stiffness: 200, damping: 20 }}
                                                />
                                            </a>

                                            {currentIndex === release.length - 1 ? (
                                                <motion.img
                                                    src=""
                                                    alt=""
                                                    className="carousel-image dummy"
                                                    animate={{ scale: 0.8, opacity: 0.3 }}
                                                />
                                            ) : (
                                                <motion.img
                                                    src={release[currentIndex + 1]?.image}
                                                    alt="Next"
                                                    className="carousel-image next"
                                                    animate={{ scale: 0.8, opacity: 0.5 }}
                                                />
                                            )}
                                        </div>

                                        <div style={{ width: "100%", textAlign: "center", marginTop: "10px" }}>
                                            {release[currentIndex].frameLink ? (
                                                <iframe
                                                    style={{
                                                        borderRadius: "12px",
                                                        display: "block",
                                                        margin: "auto",
                                                    }}
                                                    src={release[currentIndex].frameLink}
                                                    width="60%"
                                                    height="120"
                                                    frameBorder="0"
                                                    allowFullScreen=""
                                                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                                                    loading="lazy"
                                                ></iframe>
                                            ) : (
                                                <p>No Spotify preview available</p>
                                            )}
                                        </div>

                                        <div className="release-description-area" style={{ width: "100%", textAlign: "center" }}>
                                            {release[currentIndex].description}
                                        </div>

                                        <div className="dots-container">
                                            {release.map((_, index) => (
                                                <span
                                                    key={index}
                                                    className={`dot ${index === currentIndex ? "" : "active"}`}
                                                ></span>
                                            ))}
                                        </div>
                                    </Row>
                                </Col>

                                {!isMobile && (
                                    <Col xs={1} className="button-right d-flex align-items-center justify-content-center">
                                        <Button
                                            className="small-arrow-button"
                                            variant="dark"
                                            onClick={nextImage}
                                            disabled={currentIndex === release.length - 1}
                                        >
                                            <FaArrowRight size={24} />
                                        </Button>
                                    </Col>
                                )}
                            </Row>
                        </motion.div>
                    </>
                )}

                <Row>
                    <Col md={4}>
                        <div style={{ padding: "10px", marginLeft: "", float: "left" }}>
                            <FaInfoCircle
                                size={24}
                                style={{ cursor: "pointer", color: "darkgray" }}
                                onClick={() => setShowInfo(!showInfo)}
                            />
                        </div>
                    </Col>
                </Row>

                {showInfo && (
                    <Row className="mt-3 textBlockArtist">
                        <Col md={12}>
                            <div>{block1}</div>
                            <div>{block2}</div>
                            <div>{block3}</div>
                            <div>{block4}</div>
                            <div>{block5}</div>
                        </Col>
                    </Row>
                )}
            </Card.Body>
        </Card>
    );
};

export default Artist;